import { Suspense, useState, useCallback } from "react";
import { SkyLink, SkyButton } from "@skypointcloud/skypoint-ui";
import { ROUTES } from "../../../components/shared/constant";
import { useNavigate } from "react-router";
import { Navigation20Regular } from "@fluentui/react-icons";
import { useMsalAuth } from "../../../hooks/useMsalAuth";
import SidebarSky from "../sidebar/sidebar";
import { ProductWordmark, TenantLogo } from "./TenantLogo";
import {
  ProductSwitcher,
  useProductSwitcherVisible,
} from "../../../components/products/ProductSwitcher";
import { NewVersionBanner } from "../../../components/shared/NewVersionBanner";
import { useActiveProduct } from "../../../hooks/useActiveProduct";
import { PerformanceNotifications, PerformanceSearch } from "../../../routes/lazyComponents";

import { useHeaderData, useMobileDetection } from "./hooks";

import { ProfileMenu, GlobalSearchBar } from "./components";
import {
  HeaderLocationSwitcher,
  useFreshAccess,
  useLocationDeepLink,
  useLocationSwitcherVisible,
} from "./components/HeaderLocationSwitcher";
import { DEFAULT_PRODUCT } from "../../../../shared/products/keys";

/**
 * `productAdmitted`: the layout's route guard has decided and the page stays (AppLayout's
 * `admitted`). Until then nothing of a product's own is drawn (review M-2), so the Performance
 * search waits for it, as the page does. Absent means not admitted.
 */
const HeaderSky = ({ productAdmitted = false }: { productAdmitted?: boolean }) => {
  const navigate = useNavigate();
  const { logout, getQueryString } = useMsalAuth();

  const isMobileDevice = useMobileDetection();
  const { selectedInstanceId } = useHeaderData();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  useLocationDeepLink();
  useFreshAccess();
  // D3 (2026-10-08 shell spec): no Performance page filters by location, so the Performance shell
  // draws no Location control at all, on desktop or mobile. Hire's is unchanged.
  const activeProduct = useActiveProduct();
  const inPerformance = activeProduct === "skyperformance";
  // Hire's own header controls (Location, its search) belong to the hire shell only: neither
  // skyPerformance nor skyPerformance IT (spec 2026-10-26 §4.6) draws them.
  const inHire = activeProduct === DEFAULT_PRODUCT;
  const showLocationSwitcher = useLocationSwitcherVisible() && inHire;
  // Hire / Performance (spec §6, D14): only for a user holding both products on an instance that
  // has both. Everyone else gets the header exactly as it was.
  const showProductSwitcher = useProductSwitcherVisible();
  // WF-SHELL-1: hire's search finds applicants and jobs, which mean nothing in skyPerformance; it
  // draws its own (people, coaching, plans, to-dos; managers and HR only). Hire's is unchanged.
  // HRIS keeps it: its pages are in the search index (the shell's own sections), and the people it
  // is about are the same applicants.
  const showGlobalSearch = inHire || activeProduct === "hris";

  const handleViewAccount = useCallback(() => {
    navigate(ROUTES.ACCOUNT + getQueryString());
  }, [navigate, getQueryString]);

  const renderDesktopLogo = () => {
    if (isMobileDevice) return null;

    return (
      <SkyLink
        href={ROUTES.HOME + "?instanceid=" + selectedInstanceId}
        // No percentage min-width: at 130% the logo overflowed its own group and
        // the stretched search wrapper ended up on top of the Location control,
        // which then could not be clicked at all (D32).
        className="bg-header h-[52px] border-r-0 p-2 shrink-0 flex! items-center"
      >
        <TenantLogo />
      </SkyLink>
    );
  };

  return (
    <div className="min-w-full sticky top-0 z-999">
      {/* Above the toolbar and inside the sticky wrapper, so it stays visible
          while the user scrolls and pushes the app down rather than covering
          anything. Renders nothing until a newer build is actually deployed,
          and nothing at all in dev. */}
      <NewVersionBanner />

      {/* Desktop Header - visible at md (992px+) per Bootstrap breakpoints */}
      <div
        role="toolbar"
        aria-label="Header toolbar"
        className="hidden! md:flex! bg-header h-[52px] items-center gap-4 px-4 py-2 z-10000 text-header-foreground shadow-md"
      >
        {/* Two groups, neither stretching: a stretched middle group covered the
            Location control and swallowed its clicks (D32). The logo alone on the left; the
            Location control opens the right group (2026-10-08 shell spec, D2). */}
        <div className="flex shrink-0 items-center gap-4">{renderDesktopLogo()}</div>
        {/* The solution-mode badge that sat here is gone. It offered Default,
            Healthcare, Senior Living, Provider Groups and Health Plans, and
            picking one set --solution-primary-color on the document root --
            a variable no stylesheet or component in this repo reads. The
            only thing that changed was the badge's own label and colour, so
            it was a control whose sole effect was to relabel itself. */}
        {/* With the product switcher the bar overflowed by up to ~100px between 1024px and 1140px
            (Task 14 fix round 1). Only then may this group shrink, and the search box is what gives
            way (360px down to 160px); it still never stretches (D32). Without the switcher this
            group and the search box are exactly as they were. */}
        <div
          className={
            showProductSwitcher
              ? "ml-auto flex min-w-0 shrink items-center gap-4"
              : "ml-auto flex shrink-0 items-center gap-4"
          }
        >
          {showLocationSwitcher && (
            // Never the part that gives way: the search box does (360px down to 160px).
            <div className="flex shrink-0 items-center">
              <HeaderLocationSwitcher variant="header" />
            </div>
          )}
          {showGlobalSearch && <GlobalSearchBar fluid={showProductSwitcher} />}
          {inPerformance && productAdmitted && (
            <Suspense fallback={null}>
              <PerformanceSearch />
            </Suspense>
          )}
          {inPerformance && productAdmitted && (
            <Suspense fallback={null}>
              <PerformanceNotifications />
            </Suspense>
          )}
          {showProductSwitcher && <ProductSwitcher />}
          <ProfileMenu
            onViewAccount={handleViewAccount}
            onSignOut={logout}
          />
        </div>
      </div>

      {/* Mobile Header - visible below md (992px) per Bootstrap breakpoints */}
      <div className="md:hidden!">
        <div
          role="toolbar"
          aria-label="Mobile header toolbar"
          className="bg-header h-14 flex items-center justify-between px-3 z-10000 text-header-foreground"
        >
          <div className="flex items-center gap-3">
            <SkyButton
              icon={Navigation20Regular}
              appearance="transparent"
              className="text-header-foreground! min-w-10! h-10!"
              aria-label="Open menu"
              onClick={() => setSidebarOpen(true)}
            />
            <SkyLink
              href={ROUTES.HOME + "?instanceid=" + selectedInstanceId}
              className="flex items-center"
            >
              <ProductWordmark className="h-8" />
            </SkyLink>
          </div>
          <div className="flex items-center gap-2">
            {/* E10: the bell at every width (wireframe index.html:61). */}
            {inPerformance && productAdmitted && (
              <Suspense fallback={null}>
                <PerformanceNotifications />
              </Suspense>
            )}
            <ProfileMenu
              onViewAccount={handleViewAccount}
              onSignOut={logout}
            />
          </div>
        </div>
        {/* Mobile Sidebar - SidebarSky handles backdrop and drawer internally */}
        <SidebarSky
          isMobile
          isOpen={sidebarOpen}
          onOpenChange={setSidebarOpen}
          mobileTop={
            // On mobile the product pill lives in the menu with the Location switcher (D27): at 360px
            // the bar has no room for it beside the wordmark and the profile menu.
            showProductSwitcher ? (
              <div className="flex flex-col gap-3">
                <ProductSwitcher
                  variant="sheet"
                  onSwitch={() => setSidebarOpen(false)}
                />
                {showLocationSwitcher && <HeaderLocationSwitcher variant="sheet" />}
              </div>
            ) : showLocationSwitcher ? (
              <HeaderLocationSwitcher variant="sheet" />
            ) : undefined
          }
        />
      </div>
    </div>
  );
};

export default HeaderSky;
